import { NavigationLoadingLink } from '@features/components/Navigation/NavigationLoadingLink';
import usePermissionGuard from '@shared/hooks/usePermissionGuard';
import { slideUIMotion } from '@shared/motions/animations';
import { Avatar } from '@shared/ui/Avatar';
import { useAppSelector } from '@store/hooks';
import { selectTheme } from '@store/reducers/appSlice';
import { selectAuthUser } from '@store/reducers/authSlice';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { themeChange } from 'theme-change';

import { AppNavigationName } from './model/NavigationEnum';

interface MobileBottomNavigationProps {
  showLabel?: boolean;
  showLabelOnActive?: boolean;
}

const MobileBottomNavigation: FC<MobileBottomNavigationProps> = ({
  showLabel,
  showLabelOnActive,
}) => {
  const { t } = useTranslation();
  const authUser = useAppSelector(selectAuthUser);
  const theme = useAppSelector(selectTheme);
  const { hasPermission } = usePermissionGuard();

  useEffect(() => {
    themeChange(false);
    return () => {
      themeChange(false);
    };
  }, []);

  const navigationPaths: Record<AppNavigationName, string[]> = {
    [AppNavigationName.HOME]: ['/'],
    [AppNavigationName.CONNECTION]: ['/connection'],
    [AppNavigationName.QUESTION]: ['/question'],
    [AppNavigationName.DISCUSSION]: ['/discussion'],
    [AppNavigationName.MORE]: ['/more'],
  };

  const navigationLinks: Record<AppNavigationName, string> = {
    [AppNavigationName.HOME]: '/',
    [AppNavigationName.CONNECTION]: '/connection',
    [AppNavigationName.QUESTION]: '/question',
    [AppNavigationName.DISCUSSION]: '/discussion',
    [AppNavigationName.MORE]: '/more',
  };

  const isActive = (nav: AppNavigationName) => {
    return navigationPaths[nav].includes(window.location.pathname);
  };

  const renderMenuItem = (menuName: AppNavigationName, icon: React.ReactNode) => {
    return (
      <NavigationLoadingLink
        to={navigationLinks[menuName]}
        className={classNames('flex gap-2 text-xs rounded-xl', {
          'flex-1 px-2': !isActive(menuName),
          'bg-base-content dark:bg-base-300': isActive(menuName),
          'text-white': isActive(menuName),
        })}
        loadingIcon={<span className="loading loading-spinner loading-sm" />}
      >
        {icon}
        {showLabel && <span className="uppercase">{t(`navigation.${menuName}`)}</span>}
        {!showLabel && showLabelOnActive && isActive(menuName) && (
          <span className="uppercase">{t(`navigation.${menuName}`)}</span>
        )}
      </NavigationLoadingLink>
    );
  };

  return (
    <motion.div
      className="sticky bottom-0 left-0 right-0 flex justify-start items-center pb-4 pt-2 px-4 z-50 bg-transparent shadow-lg w-full"
      {...slideUIMotion}
    >
      <ul className="menu menu-horizontal bg-base-100/55 border border-base-100/85 backdrop-blur-xl rounded-2xl gap-2 shadow-lg shadow-base-300 flex-1 justify-around">
        <li>
          {renderMenuItem(
            AppNavigationName.HOME,
            <div className="icon-[solar--home-smile-linear] h-6 w-6" />,
          )}
        </li>
        <li>
          {renderMenuItem(
            AppNavigationName.QUESTION,
            <div className="icon-[solar--document-add-linear] h-6 w-6" />,
          )}
        </li>
        {hasPermission('connections.manage') && (
          <li>
            {renderMenuItem(
              AppNavigationName.CONNECTION,
              <div className="icon-[solar--user-heart-broken] h-6 w-6" />,
            )}
          </li>
        )}
        {hasPermission('posts.manage') && (
          <li>
            {renderMenuItem(
              AppNavigationName.DISCUSSION,
              <div className="icon-[solar--square-academic-cap-outline] h-6 w-6" />,
            )}
          </li>
        )}
        <li>
          {renderMenuItem(
            AppNavigationName.MORE,
            authUser ? (
              <Avatar size="xs" shape="rounded" uuid={authUser.id} mode={theme} />
            ) : (
              <div className="icon-[solar--user-circle-outline] h-6 w-6" />
            ),
          )}
        </li>
      </ul>
    </motion.div>
  );
};

export default MobileBottomNavigation;
